import { useMemo } from 'react'
import { useProfile } from '../../Layout/UserProfile/UserProfileProvider'
import { CaseType, dvsWorkflow } from '../Services/ConstantData'
import { CaseResponse } from '../Common/Queries'
import { useEnv } from '@praxis/component-runtime-env'
import { EnvConfig } from '../../../config/appConfig'

export enum WorkflowType {
  TM_DISPUTE = 'TM_DISPUTE',
  TM_CLAIM = 'TM_CLAIM',
  TM_REQUEST = 'TM_REQUEST',
  BP_DISPUTE = 'BP_DISPUTE',
  BP_REQUEST = 'BP_REQUEST',
  UNKNOWN = 'UNKNOWN',
}

/** Default workflows eligible for reopen (used when config value is absent) */
const DEFAULT_REOPEN_ELIGIBLE_WORKFLOWS = ['ELECT', 'TOYS']

interface UseWorkflowResult {
  /** The resolved workflow type for the case */
  workflowType: WorkflowType
  /** The raw workflow string from the case (e.g., 'TCLAIM', 'DEFAULT', 'TGTCOM') */
  workflowValue: string | undefined
  /** Whether the case belongs to the DVS (TGTCOM) workflow */
  isDvsWorkflow: boolean
  /** Whether the case is a Team Member (internal) workflow */
  isTeamMemberWorkflow: boolean
  /** Whether the case is a Business Partner (vendor) workflow */
  isBusinessPartnerWorkflow: boolean
  /** Whether reopen is enabled (only for BP view with ELECT or TOYS workflows) */
  isReopenEnabled: boolean
}

/**
 * Custom hook to determine which workflow a case belongs to.
 * Evaluates based on the case type, workflow assignment, and user profile (TM vs BP).
 *
 * @param caseData - The case response object containing caseType and workflow fields
 * @returns An object with workflow type, raw value, and boolean helpers
 */
export default function useWorkflow(
  caseData: CaseResponse | undefined | null,
): UseWorkflowResult {
  const { userProfile } = useProfile()
  const env = useEnv() as EnvConfig
  const reopenEligibleWorkflows =
    env.reopenEligibleWorkflows ?? DEFAULT_REOPEN_ELIGIBLE_WORKFLOWS

  const workflowResult = useMemo<UseWorkflowResult>(() => {
    const workflowValue = caseData?.workflow
    const caseType = caseData?.caseType
    const isVendor = userProfile?.isVendor ?? false
    const isDvsWorkflow = workflowValue === dvsWorkflow
    const isReopenEnabled =
      isVendor && reopenEligibleWorkflows.includes(workflowValue ?? '')

    let workflowType: WorkflowType = WorkflowType.UNKNOWN

    if (!caseType) {
      return {
        workflowType: WorkflowType.UNKNOWN,
        workflowValue,
        isDvsWorkflow,
        isTeamMemberWorkflow: false,
        isBusinessPartnerWorkflow: false,
        isReopenEnabled,
      }
    }

    if (!isVendor) {
      // Team Member workflows
      switch (caseType) {
        case CaseType.DISPUTE:
          workflowType = WorkflowType.TM_DISPUTE
          break
        case CaseType.INTERNALPROCESSING:
          workflowType = WorkflowType.TM_CLAIM
          break
        case CaseType.REQUESTFORINFO:
          workflowType = WorkflowType.TM_REQUEST
          break
        default:
          workflowType = WorkflowType.UNKNOWN
      }
    } else {
      // Business Partner workflows
      switch (caseType) {
        case CaseType.DISPUTE:
          workflowType = WorkflowType.BP_DISPUTE
          break
        case CaseType.REQUESTFORINFO:
          workflowType = WorkflowType.BP_REQUEST
          break
        default:
          workflowType = WorkflowType.UNKNOWN
      }
    }

    return {
      workflowType,
      workflowValue,
      isDvsWorkflow,
      isTeamMemberWorkflow: !isVendor,
      isBusinessPartnerWorkflow: isVendor,
      isReopenEnabled,
    }
  }, [
    caseData?.workflow,
    caseData?.caseType,
    userProfile?.isVendor,
    reopenEligibleWorkflows,
  ])

  return workflowResult
}
